import { useNode, useEditor, type Node, ROOT_NODE, type NodeId } from '@craftjs/core';
import cx from 'classnames';
import debounce from 'debounce';
import { Resizable, type ResizeCallback, type ResizeStartCallback } from 're-resizable';
import { useRef, useEffect, useState, useCallback } from 'react';

import {
  getEffectiveMaxWidthPx,
  isPercentage,
  pxToPercent,
  percentToPx,
  getElementDimensionsWithoutPadding,
  getElementDimensionsWithPadding,
} from '../../utils/numToMeasurement';
import { getNodeThemeColorClass, outlineColorClasses } from '@/utils/constants';

import { useContainerHelper } from '@/hooks/useContainerHelper';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  emptyContainer,
  getAdjacentFillableSibling,
  parseWidthToNumber,
  isContainerNode,
  isImageNode,
  isFormNode,
  getBoundaryDomElement,
  getNodeTopLeftInParentElement,
  isRelativelyFillableElementV2,
  hasFixedHeightV2,
  isButtonNode,
} from '@/utils/helperFunc';
import type { ContainerProps } from '@builder4/shared/components/Container';
import { ImageRadiusControls } from './Image/ImageRadiusControls';
import { selectScale } from '@/store/zoom/selectors';
import { setObjectResizing } from '@/store/system/systemSlice';
import { selectEffectiveBreakpoint, selectIsFullscreenPreview } from '@/store/system/selectors';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_GAP, TABLET_WIDTH, DESKTOP_WIDTH } from '@builder4/shared/utils/constants';

type Position =
  | 'top'
  | 'left'
  | 'bottom'
  | 'right'
  | 'bottomRight'
  | 'bottomLeft'
  | 'topRight'
  | 'topLeft';

export type PositionFlags = Record<Position, boolean>;

/** Normalize resize direction to horizontal for sibling selection (left vs right). */
const widthDirection = (direction?: Position): 'left' | 'right' | undefined => {
  if (direction == null) return undefined;
  const d = direction.toLowerCase();
  if (d.includes('left')) return 'left';
  if (d.includes('right')) return 'right';
  return undefined;
};

type Dimensions = {
  width: number;
  height: number;
  top: number;
  left: number;
  right: number;
  bottom: number;
};
interface ResizerProps {
  propKey: {
    width: string;
    height: string;
  };
  children: React.ReactNode;
  onRef?: (ref: HTMLElement) => void;
  [key: string]: unknown;
}

// Selects the width prop string for a given parent width in fullscreen, mirroring the
// @container thresholds used on the published page. Separated for testability.
export function selectFullscreenWidthProp(
  nodeProps: Partial<ContainerProps>,
  nodeWidth: string,
  parentWidth: number,
): string {
  if (parentWidth < TABLET_WIDTH) {
    // Mirror @container (max-width: 767px) — use mobile width, defaulting to 100%
    return String(nodeProps.mobile?.width ?? '100%');
  }
  if (parentWidth < DESKTOP_WIDTH) {
    // Mirror @container (min-width: 768px) and (max-width: 1199px) — use tablet width
    return String(nodeProps.tablet?.width ?? nodeWidth);
  }
  return nodeWidth;
}

export const Resizer = ({ propKey, children, onRef, maxWidth, ...props }: ResizerProps) => {
  const dispatch = useAppDispatch();
  const breakpoint = useAppSelector(selectEffectiveBreakpoint);
  const isFullscreenPreview = useAppSelector(selectIsFullscreenPreview);
  const [minHeight, setMinHeight] = useState(0);
  const [maxHeight, setMaxHeight] = useState(0);
  const [maxWidthLocal, setMaxWidth] = useState(maxWidth as string);
  const { getTallestChild, getFillableChilds, getIndexInFillableChilds } = useContainerHelper();
  const { getNodeContentDimensions } = useCraftHelper();
  const {
    currentNode,
    id,
    parent,
    isContentBlock,
    isImage,
    isForm,
    nodes,
    nodeName,
    nodeProps,
    isLocked,
    actions: { setProp },
  } = useNode((node) => ({
    parent: node.data.parent,
    active: node.events.selected,
    isContentBlock: isContainerNode(node as Node),
    isImage: isImageNode(node as Node),
    isForm: isFormNode(node as Node),
    nodes: node.data.nodes,
    nodeName: node.data.displayName,
    nodeProps: node.data.props as Partial<ContainerProps>,
    isLocked: node.data.custom.isLocked,
    currentNode: node as Node,
  }));

  const { getBPProp, setBPProp } = useBreakpointAwareProp(nodeName);
  // Read props through breakpoint-aware system (handles both legacy and new components)
  const nodeWidth = getBPProp(
    nodeProps,
    propKey.width as keyof Partial<ContainerProps> as keyof Partial<ContainerProps>,
  ) as string;
  const nodeHeight = getBPProp(
    nodeProps,
    propKey.height as keyof Partial<ContainerProps> as keyof Partial<ContainerProps>,
  ) as string;
  const fillSpace = getBPProp(nodeProps, 'fillSpace');
  const aspectRatioLock = getBPProp(nodeProps, 'aspectRatioLock');
  const aspectRatio = getBPProp(nodeProps, 'aspectRatio');
  const isAspectRatioLocked = aspectRatioLock && aspectRatio && aspectRatio > 0;
  const padding = getBPProp(nodeProps, 'padding') ?? [0, 0, 0, 0];
  const margin = getBPProp(nodeProps, 'margin') ?? [0, 0, 0, 0];
  const borderWidth = getBPProp(nodeProps, 'borderWidth') || 0;
  const flexDirection = getBPProp(nodeProps, 'flexDirection');
  const gap = getBPProp(nodeProps, 'gap') || 0;
  const position = getBPProp(nodeProps, 'position');
  const positionAnchorsState = getBPProp(nodeProps, 'positionAnchorsState')!;
  const isAbsolutelyPositioned = position === 'absolute' || position === 'fixed';

  const {
    actions,
    query: { node },
    parentDirection,
    parentNode,
    isActive,
  } = useEditor((_, query) => {
    const parentId = typeof parent === 'string' && parent.length > 0 ? parent : ROOT_NODE;
    const parentQuery = query.node(parentId);
    const parentNode = parentQuery?.get();
    return {
      parentDirection: parentNode
        ? getBPProp(parentNode.data.props as Partial<ContainerProps>, 'flexDirection')
        : undefined,
      isRootNode: !id ? false : query.node(id).isRoot(),
      parentNode: parentNode,
      isActive: !id ? false : query.getEvent('selected').contains(id),
    };
  });

  const scale = useAppSelector(selectScale);

  const resizable = useRef<Resizable>(null);
  const isResizing = useRef<boolean>(false);
  const changeHeightCalled = useRef<boolean>(false);
  const dimensionOnResizeStartInPx = useRef<Dimensions | null>(null);
  const nodeDimensions = useRef<{ width: string; height: string } | null>(null);
  nodeDimensions.current = {
    width: nodeWidth,
    height: nodeHeight,
  };
  const [internalDimensions, setInternalDimensions] = useState({
    width: nodeWidth,
    height: nodeHeight,
  });

  // WHY: In fullscreen preview the canvas IS the viewport, so CSS @container queries fire based
  // on real viewport width. Re-resizable always writes style.width as an inline style, which
  // overrides CSS class rules — including @container responsive overrides. To match published
  // page behaviour we compute the correct responsive width in JS by mirroring the container
  // query thresholds.
  //
  // WHY window.innerWidth for breakpoint selection (not parentElement.offsetWidth): @container
  // queries are scoped to the page-node root container, whose width tracks the full viewport.
  // Nested containers can have a parent narrower than the breakpoint thresholds (e.g. a 50%
  // column is ~600px on a 1200px desktop page), which would incorrectly trigger the tablet or
  // mobile breakpoint if we compared against parentWidth. parentWidth is still used for the
  // percentage-to-px conversion, since percentage widths are always relative to the parent.
  const computeFullscreenSizeWidth = useCallback((): string => {
    const dom = resizable.current?.resizable;
    if (!dom?.parentElement) return nodeWidth;
    const parentWidth = getElementDimensionsWithoutPadding(dom.parentElement as HTMLElement).width;
    if (!parentWidth) return nodeWidth;
    return percentToPx(
      selectFullscreenWidthProp(nodeProps, nodeWidth, window.innerWidth),
      parentWidth,
    );
  }, [nodeProps, nodeWidth]);

  const [fullscreenSizeWidth, setFullscreenSizeWidth] = useState<string>(nodeWidth);

  useEffect(() => {
    if (isFullscreenPreview) {
      setFullscreenSizeWidth(computeFullscreenSizeWidth());
    }
  }, [isFullscreenPreview, computeFullscreenSizeWidth]);

  useEffect(() => {
    if (!isFullscreenPreview) return;
    const listener = debounce(() => setFullscreenSizeWidth(computeFullscreenSizeWidth()), 1);
    const visibilityListener = () => {
      if (document.visibilityState === 'visible') {
        setFullscreenSizeWidth(computeFullscreenSizeWidth());
      }
    };
    window.addEventListener('resize', listener);
    document.addEventListener('visibilitychange', visibilityListener);
    return () => {
      window.removeEventListener('resize', listener);
      document.removeEventListener('visibilitychange', visibilityListener);
    };
  }, [isFullscreenPreview, computeFullscreenSizeWidth]);

  useEffect(() => {
    setMaxWidth(maxWidth as string);
  }, [maxWidth]);

  const updateInternalDimensionsInPx = useCallback(() => {
    if (!nodeDimensions.current) return;
    const { width: nodeWidth, height: nodeHeight } = nodeDimensions.current;

    const width = percentToPx(
      String(nodeWidth),
      resizable.current?.resizable?.parentElement
        ? getElementDimensionsWithoutPadding(resizable.current.resizable.parentElement).width
        : 0,
    );
    const height = percentToPx(
      String(nodeHeight),
      resizable.current?.resizable?.parentElement
        ? getElementDimensionsWithoutPadding(resizable.current.resizable.parentElement).height
        : 0,
    );

    setInternalDimensions({
      width,
      height,
    });
  }, []);

  const updateMaxWidthInPx = useCallback(
    (preferredDirection?: Position) => {
      const dom = resizable.current?.resizable;
      if (!dom) return;
      const parentElement = dom.parentElement as HTMLElement;
      if (!parentElement) return;

      const parentWidth = getElementDimensionsWithoutPadding(parentElement).width;

      // Row with siblings: sibling min = 5% parent; max = current + (sibling - 5% parent) - gap*2
      let rowMaxPx: number | null = null;
      if (
        parentDirection === 'row' &&
        parentNode &&
        isRelativelyFillableElementV2(currentNode, breakpoint)
      ) {
        const siblingNode = getAdjacentFillableSibling(
          id,
          getFillableChilds(parentNode.id as NodeId),
          widthDirection(preferredDirection),
        );
        if (siblingNode?.dom) {
          const parentGap = getBPProp(parentNode.data.props, 'gap') || 0;
          const currentWidthPx = getElementDimensionsWithPadding(dom).width;
          const siblingWidthPx = getElementDimensionsWithPadding(
            siblingNode.dom as HTMLElement,
          ).width;
          const minWidthPx = getBPProp(nodeProps, 'minWidth');
          const minSiblingPx =
            minWidthPx != null && Number.isFinite(minWidthPx) ? minWidthPx : 0.05 * parentWidth;
          rowMaxPx = Math.max(
            0,
            currentWidthPx + Math.max(0, siblingWidthPx - minSiblingPx) - parentGap * 2,
          );
        }
      }

      const effectiveMaxPx = getEffectiveMaxWidthPx(
        parentWidth,
        maxWidth as string | number | undefined,
        rowMaxPx,
      );
      setMaxWidth(effectiveMaxPx + 'px');
    },
    [
      maxWidth,
      parentDirection,
      parentNode,
      currentNode,
      breakpoint,
      id,
      getFillableChilds,
      nodeProps,
      nodeName,
    ],
  );

  const updateInternalDimensionsWithOriginal = useCallback(() => {
    if (!nodeDimensions.current) return;
    const { width: nodeWidth, height: nodeHeight } = nodeDimensions.current;
    setInternalDimensions({
      width: nodeWidth,
      height: nodeHeight,
    });
  }, []);

  const updateMaxWidthWithOriginal = useCallback(() => {
    setMaxWidth(maxWidth as string);
  }, [updateMaxWidthInPx]);

  const getUpdatedDimensions = (
    deltaWidth: number,
    deltaHeight: number,
  ): Dimensions | undefined => {
    const dom = resizable.current?.resizable;
    if (!dom || !dimensionOnResizeStartInPx.current) return;

    const currentWidth = parseInt(String(dimensionOnResizeStartInPx.current.width));
    const currentHeight = parseInt(String(dimensionOnResizeStartInPx.current.height));
    const position = {
      left: (dimensionOnResizeStartInPx.current.left || 0) - parseInt(String(deltaWidth)),
      top: (dimensionOnResizeStartInPx.current.top || 0) - parseInt(String(deltaHeight)),
      right: (dimensionOnResizeStartInPx.current.right || 0) - parseInt(String(deltaWidth)),
      bottom: (dimensionOnResizeStartInPx.current.bottom || 0) - parseInt(String(deltaHeight)),
    };
    return {
      width: currentWidth + parseInt(String(deltaWidth)),
      height: currentHeight + parseInt(String(deltaHeight)),
      ...position,
    };
  };

  const didDrag = useRef(false);
  const siblingIdOnResize = useRef<string | null>(null);
  const adjacentSiblingWidthPercentage = useRef<number | null>(null);

  useEffect(() => {
    if (!isResizing.current) updateInternalDimensionsWithOriginal();
  }, [nodeWidth, nodeHeight, updateInternalDimensionsWithOriginal]);

  useEffect(() => {
    const listener = debounce(updateInternalDimensionsWithOriginal, 1);
    window.addEventListener('resize', listener);

    return () => {
      window.removeEventListener('resize', listener);
    };
  }, [updateInternalDimensionsWithOriginal]);

  const allowedControls: Partial<PositionFlags> = {};
  if (isActive && !isLocked) {
    if (isRelativelyFillableElementV2(currentNode, breakpoint)) {
      if (parentDirection === 'row') {
        const index = getIndexInFillableChilds(id);
        allowedControls.left = index !== 0;
        allowedControls.right = index !== getFillableChilds(parentNode?.id as NodeId).length - 1;
      }
    } else {
      allowedControls.left = true;
      allowedControls.right = true;
    }

    allowedControls.bottom = true;
    allowedControls.top = true;
  }

  // Enable corner handles whenever the corresponding edges are enabled
  allowedControls.topLeft = Boolean(allowedControls.top && allowedControls.left);
  allowedControls.topRight = Boolean(allowedControls.top && allowedControls.right);
  allowedControls.bottomLeft = Boolean(allowedControls.bottom && allowedControls.left);
  allowedControls.bottomRight = Boolean(allowedControls.bottom && allowedControls.right);

  const willChangeWidth = (direction: Position) => {
    return isAspectRatioLocked || direction.includes('left') || direction.includes('right');
  };

  const willChangeHeight = (direction: Position) => {
    return isAspectRatioLocked || direction.includes('top') || direction.includes('bottom');
  };

  const shouldAdjustSiblingWidth = (direction: Position) => {
    const currentNode = node(id)?.get() as Node;
    if (!currentNode) return false;
    return (
      parentDirection === 'row' &&
      willChangeWidth(direction) &&
      isRelativelyFillableElementV2(currentNode, breakpoint) &&
      siblingIdOnResize.current &&
      adjacentSiblingWidthPercentage.current !== null
    );
  };

  // Helper function to convert pixel value to dimension string (px or %)
  const convertPxToDimensionString = (
    valueInPx: number,
    originalProp: string,
    parentDimension: number,
  ): string => {
    if (originalProp && isPercentage(originalProp)) {
      return pxToPercent(valueInPx, parentDimension) + '%';
    }
    return `${Math.round(valueInPx)}px`;
  };

  const getNewFinalDimensions = (deltaDimensions: {
    width: number;
    height: number;
  }): {
    newFinalWidthStr: string;
    newFinalHeightStr: string;
    left: number;
    top: number;
    right: number;
    bottom: number;
  } => {
    const dom = resizable.current?.resizable;
    if (!dom)
      return { newFinalWidthStr: '', newFinalHeightStr: '', left: 0, top: 0, right: 0, bottom: 0 };

    const updatedDimensionsInPx = getUpdatedDimensions(
      deltaDimensions.width,
      deltaDimensions.height,
    );

    if (!updatedDimensionsInPx)
      return { newFinalWidthStr: '', newFinalHeightStr: '', left: 0, top: 0, right: 0, bottom: 0 };

    const { width, height, top, left, right, bottom } = updatedDimensionsInPx;

    // Cache parent dimensions (used multiple times)
    const parentElement = dom.parentElement as HTMLElement;
    const parentDimensions = parentElement
      ? getElementDimensionsWithoutPadding(parentElement)
      : { width: 0, height: 0 };

    // Use helper for conversion
    let newFinalWidthStr = convertPxToDimensionString(width, nodeWidth, parentDimensions.width);
    let newFinalHeightStr = convertPxToDimensionString(height, nodeHeight, parentDimensions.height);

    // Handle auto parent dimensions edge cases
    if (
      isPercentage(newFinalWidthStr) &&
      dom.parentElement?.style.width === 'auto' &&
      dimensionOnResizeStartInPx.current
    ) {
      newFinalWidthStr = `${dimensionOnResizeStartInPx.current.width + deltaDimensions.width}px`;
    }

    if (
      isPercentage(newFinalHeightStr) &&
      dom.parentElement?.style.height === 'auto' &&
      dimensionOnResizeStartInPx.current
    ) {
      newFinalHeightStr = `${dimensionOnResizeStartInPx.current.height + deltaDimensions.height}px`;
    }

    // Clamp width to 100% when element uses percentage width
    if (isPercentage(String(nodeWidth)) && isPercentage(newFinalWidthStr)) {
      const percent = parseFloat(newFinalWidthStr.replace('%', ''));
      if (Number.isFinite(percent) && percent > 100) {
        newFinalWidthStr = '100%';
      }
    }

    return {
      newFinalWidthStr,
      newFinalHeightStr,
      left,
      top,
      right,
      bottom,
    };
  };

  const setupSiblingTracking = useCallback(
    (preferredDirection?: Position) => {
      if (!parentNode) return;
      const siblingNode = getAdjacentFillableSibling(
        id,
        getFillableChilds(parentNode.id as NodeId),
        widthDirection(preferredDirection),
      );
      if (!siblingNode) {
        siblingIdOnResize.current = null;
        adjacentSiblingWidthPercentage.current = null;
        return;
      }
      siblingIdOnResize.current = siblingNode.id;
      const currentNode = node(id).get();
      const currentWidthStr = getBPProp(currentNode.data.props, 'width');
      const siblingWidthStr = getBPProp(siblingNode.data.props, 'width');
      const currentPercent = parseWidthToNumber(currentWidthStr || '0%');
      const siblingPercent = parseWidthToNumber(siblingWidthStr || '0%');
      adjacentSiblingWidthPercentage.current = currentPercent + siblingPercent;
    },
    [id, parentNode, getFillableChilds, node, getBPProp, parseWidthToNumber],
  );

  const adjustSiblingWidth = (newFinalWidthStr: string) => {
    const mergeActions = actions.history.merge();

    let newCurrentPercent = parseFloat(String(newFinalWidthStr).replace('%', ''));

    // Clamp to minimum
    const minPercent = 5;
    newCurrentPercent = Math.max(newCurrentPercent, minPercent);

    // Sibling gets the rest
    let newSiblingPercent = (adjacentSiblingWidthPercentage.current || 0) - newCurrentPercent;
    newSiblingPercent = Math.max(newSiblingPercent, minPercent);

    // If clamping sibling caused current to go over, adjust
    if (newCurrentPercent + newSiblingPercent > (adjacentSiblingWidthPercentage.current || 0)) {
      newCurrentPercent = (adjacentSiblingWidthPercentage.current || 0) - newSiblingPercent;
      mergeActions.setProp(
        id,
        (props: { [key: string]: unknown; width?: string; height?: string }) => {
          setBPProp(props, propKey.width as keyof Partial<ContainerProps>, `${newCurrentPercent}%`);
          setBPProp(props, 'flexBasis', `${newCurrentPercent}%`);
        },
      );
    }

    const siblingNode = node(siblingIdOnResize.current || '')?.get();
    if (siblingNode) {
      mergeActions.setProp(siblingIdOnResize.current || '', (props: Record<string, unknown>) => {
        setBPProp(
          props,
          propKey.width as keyof Partial<ContainerProps>,
          `${newSiblingPercent}%`,
          siblingNode.data.displayName,
        );
        setBPProp(props, 'flexBasis', `${newSiblingPercent}%`, siblingNode.data.displayName);
      });
    }
  };

  function setContainerMinHeight() {
    if (flexDirection === 'column') {
      //accumulate height of all children
      let totalHeight = padding[0] + padding[2] + borderWidth * 2;

      nodes.forEach((id: string) => {
        const childNode = node(id)?.get();
        if (!childNode || !childNode.dom) return;

        const position = getBPProp(childNode.data.props as Partial<ContainerProps>, 'position');
        if (position === 'relative' || position === 'sticky') {
          totalHeight += childNode.dom.getBoundingClientRect().height / scale;
          totalHeight += gap;
        }
      });
      if (nodes.length > 0) {
        totalHeight -= gap; // minus last element gap
      }
      setMinHeight(totalHeight);
    } else {
      const tallestChild = getTallestChild(id);
      if (tallestChild?.dom) {
        const childHeight = tallestChild.dom.getBoundingClientRect().height / scale;
        setMinHeight(childHeight + padding[0] + padding[2] + borderWidth * 2);
      }
    }
  }

  function setFormMinHeight() {
    // Form uses flex column layout - accumulate height of all children (fields + submit button)
    let totalHeight = padding[0] + padding[2] + borderWidth * 2;

    nodes.forEach((childId: string) => {
      const childNode = node(childId)?.get();
      if (childNode?.dom) {
        totalHeight += childNode.dom.getBoundingClientRect().height / scale;
        totalHeight += FORM_GAP;
      }
    });
    if (nodes.length > 0) {
      totalHeight -= FORM_GAP; // minus last element gap
    }
    setMinHeight(totalHeight);
  }

  function updateHeightOnce(deltaHeight: number, heightPropKey: string, newFinalHeightStr: string) {
    if (deltaHeight == 0) return;
    if (changeHeightCalled.current) return;
    changeHeightCalled.current = true; //prevent calling this function multiple times
    actions.history
      .ignore()
      .setProp(id, (props: { [key: string]: unknown; width?: string; height?: string }) => {
        //uncomment if adjusting width height of non container or image nodes
        // if (deltaDimensions.width != 0 && !isRelativelyFillableElementV2(node(id)?.get() as Node, breakpoint)) {
        //   prop[propKey.width as keyof Partial<ContainerProps>] = newFinalWidthStr;
        //   prop.flexBasis = newFinalWidthStr;
        // }

        setBPProp(props, heightPropKey, newFinalHeightStr);
      });
  }

  const handleResizeStart: ResizeStartCallback = (e, direction) => {
    changeHeightCalled.current = false;
    e.preventDefault();
    e.stopPropagation();
    const dom = resizable.current?.resizable;
    if (!dom) return;
    const currentNode = node(id).get();

    dom.classList.remove('pointer-events-none');

    dispatch(setObjectResizing(true));
    updateInternalDimensionsInPx();
    updateMaxWidthInPx(direction);
    const bounding = dom.getBoundingClientRect();
    const topLeftPosition = getNodeTopLeftInParentElement(parentNode, currentNode, scale);
    dimensionOnResizeStartInPx.current = {
      width: bounding.width / scale,
      height: bounding.height / scale,
      left: topLeftPosition.left,
      top: topLeftPosition.top,
      right: topLeftPosition.right,
      bottom: topLeftPosition.bottom,
    };
    isResizing.current = true;
    didDrag.current = false; // Reset drag flag

    //set non root containers width to actual occupied width
    if (isRelativelyFillableElementV2(currentNode, breakpoint) && parent !== ROOT_NODE) {
      //set widths as actually occupied in percents
      const fillableSiblings = getFillableChilds(parentNode?.id as NodeId);
      const parentElement = getBoundaryDomElement(parentNode?.dom as HTMLElement);
      const parentWidth = getElementDimensionsWithoutPadding(parentElement).width;

      fillableSiblings.forEach((siblingNode: Node) => {
        if (siblingNode.dom) {
          const widthInPx = getElementDimensionsWithPadding(siblingNode.dom).width;
          const widthInPercent = pxToPercent(widthInPx, parentWidth);
          actions.history.ignore().setProp(siblingNode.id, (props: Partial<ContainerProps>) => {
            setBPProp(
              props,
              propKey.width as keyof Partial<ContainerProps>,
              `${widthInPercent}%`,
              siblingNode.data.displayName,
            );
            const fillSpace = getBPProp(props, 'fillSpace');
            if (fillSpace === 'yes')
              setBPProp(props, 'flexBasis', `${widthInPercent}%`, siblingNode.data.displayName);
          });
          if (siblingNode.dom) {
            siblingNode.dom.style.width = `${widthInPercent}%`;
            const fillSpace = getBPProp(
              siblingNode.data.props as Partial<ContainerProps>,
              'fillSpace',
            );
            if (fillSpace === 'yes') {
              siblingNode.dom.style.flex = `1 1 ${widthInPercent}%`;
            }
          }
        }
      });
    }

    if (isRelativelyFillableElementV2(currentNode, breakpoint)) {
      if (willChangeWidth(direction)) {
        setupSiblingTracking(direction);
      }

      if (willChangeHeight(direction)) {
        if (
          isRelativelyFillableElementV2(currentNode, breakpoint) &&
          parent !== ROOT_NODE &&
          hasFixedHeightV2(
            getBPProp(parentNode.data.props as Partial<ContainerProps>, 'height') ?? '',
          )
        ) {
          setMaxHeight(getNodeContentDimensions(parentNode as Node).height);
        }
        //set min height in case container height is being changed
        if (isContentBlock && !emptyContainer(currentNode)) {
          setContainerMinHeight();
        }
        // set min height for form based on its content (fields + button)
        if (isForm) {
          setFormMinHeight();
        }
      }
    }
  };

  const handleOnResize: ResizeCallback = (_, direction, ___, deltaDimensions) => {
    const { newFinalWidthStr, newFinalHeightStr } = getNewFinalDimensions(deltaDimensions);

    didDrag.current = deltaDimensions.width !== 0 || deltaDimensions.height !== 0;

    const dom = resizable.current?.resizable;
    if (!dom) return;

    if (deltaDimensions.height !== 0) {
      updateHeightOnce(deltaDimensions.height, propKey.height, newFinalHeightStr);
    }

    //adjust sibling width of sibling if width is being changed it will skip if current node is positioned absolutely
    // Normalize direction: left edge/corners use 'left', right edge/corners use 'right'
    const directionLower = direction.toLowerCase();
    const normalizedDirection = directionLower.includes('left')
      ? 'left'
      : directionLower.includes('right')
        ? 'right'
        : direction;

    if (shouldAdjustSiblingWidth(normalizedDirection)) {
      let newCurrentPercent = parseFloat(String(newFinalWidthStr).replace('%', ''));

      // Clamp to minimum
      const minPercent = 5;
      newCurrentPercent = Math.max(newCurrentPercent, minPercent);

      // Sibling gets the rest
      let newSiblingPercent = (adjacentSiblingWidthPercentage.current || 0) - newCurrentPercent;
      newSiblingPercent = Math.max(newSiblingPercent, minPercent);

      // If clamping sibling caused current to go over, adjust
      if (newCurrentPercent + newSiblingPercent > (adjacentSiblingWidthPercentage.current || 0)) {
        newCurrentPercent = (adjacentSiblingWidthPercentage.current || 0) - newSiblingPercent;
        const dom = resizable.current?.resizable;
        if (dom) {
          if (fillSpace == 'yes') {
            dom.style.flex = `1 1 ${newCurrentPercent}%`;
          }
          dom.style.width = `${newCurrentPercent}%`;
        }
      }
      const siblingNode = node(siblingIdOnResize.current || '')?.get();
      if (siblingNode?.dom) {
        const fillSpace = getBPProp(siblingNode.data.props as Partial<ContainerProps>, 'fillSpace');

        if (fillSpace == 'yes') {
          siblingNode.dom.style.flex = `1 0 ${newSiblingPercent}%`;
        }
        siblingNode.dom.style.width = `${newSiblingPercent}%`;
      }
    }
  };

  const handleOnResizeStop: ResizeCallback = (_, direction, ___, deltaDimensions) => {
    try {
      changeHeightCalled.current = false;
      const dom = resizable.current?.resizable;
      if (dom) {
        dom.classList.add('pointer-events-none');
      }

      updateInternalDimensionsWithOriginal();
      updateMaxWidthWithOriginal();
      if (!didDrag.current) return;
      const { newFinalWidthStr, newFinalHeightStr } = getNewFinalDimensions(deltaDimensions);
      isResizing.current = false;

      if (isRelativelyFillableElementV2(currentNode, breakpoint) && parentNode) {
        const fillableSiblings = getFillableChilds(parentNode.id as NodeId);
        fillableSiblings.forEach((siblingNode: Node) => {
          if (isButtonNode(siblingNode) && siblingNode.dom) {
            siblingNode.dom.style.width = '';
            siblingNode.dom.style.flex = '';
          }
        });
      }

      setProp((props: { [key: string]: unknown; width?: string; height?: string }) => {
        if (deltaDimensions.width !== 0) {
          setBPProp(props, propKey.width as keyof Partial<ContainerProps>, newFinalWidthStr);
          if (fillSpace === 'yes') {
            setBPProp(props, 'flexBasis', newFinalWidthStr);
          }
        }
        if (deltaDimensions.height !== 0) {
          const newHeight = parseInt(String(newFinalHeightStr).replace('px', ''));
          if (Math.abs(newHeight - minHeight) <= 5) {
            setBPProp(props, propKey.height, 'auto');
          } else {
            setBPProp(props, propKey.height, newFinalHeightStr);
          }
        }
      });

      // Normalize direction for sibling adjustment when width changed (edge or corner)
      let directionForSibling = direction;
      if (deltaDimensions.width !== 0) {
        const directionLower = direction.toLowerCase();
        directionForSibling = directionLower.includes('left') ? 'left' : 'right';
      }

      if (shouldAdjustSiblingWidth(directionForSibling)) {
        adjustSiblingWidth(newFinalWidthStr);
      }
    } finally {
      setTimeout(() => {
        dispatch(setObjectResizing(false));
      }, 100);
    }
  };

  const anchorsStateOnResizeStart = useRef({
    enableLeft: false,
    enableRight: false,
    enableTop: false,
    enableBottom: false,
  });

  const handleResizeStartAbsolute: ResizeStartCallback = (e, direction) => {
    changeHeightCalled.current = false;
    e.preventDefault();
    e.stopPropagation();
    const dom = resizable.current?.resizable;
    if (!dom) return;
    dom.style.flexShrink = '0';
    const currentNode = node(id).get();

    dom.classList.remove('pointer-events-none');

    dispatch(setObjectResizing(true));
    updateInternalDimensionsInPx();
    const bounding = dom.getBoundingClientRect();
    const position = getNodeTopLeftInParentElement(parentNode, currentNode, scale);
    dimensionOnResizeStartInPx.current = {
      width: bounding.width / scale,
      height: bounding.height / scale,
      left: position.left,
      top: position.top,
      right: position.right,
      bottom: position.bottom,
    };
    anchorsStateOnResizeStart.current = {
      enableLeft: positionAnchorsState.enableLeft,
      enableRight: positionAnchorsState.enableRight,
      enableTop: positionAnchorsState.enableTop,
      enableBottom: positionAnchorsState.enableBottom,
    };
    isResizing.current = true;
    didDrag.current = false; // Reset drag flag
    if (positionAnchorsState.enableLeft && positionAnchorsState.enableRight) {
      dom.style.width = dimensionOnResizeStartInPx.current.width + 'px';
    } else if (positionAnchorsState.enableTop && positionAnchorsState.enableBottom) {
      dom.style.height = dimensionOnResizeStartInPx.current.height + 'px';
    }

    switch (direction) {
      case 'left':
        //lock right
        //remove right on stop
        if (!positionAnchorsState.enableRight) {
          dom.style.right = `${dimensionOnResizeStartInPx.current.right}px`;
        }
        // props.left = undefined;//disable left on start but enable again on stop
        dom.style.left = 'unset';
        break;
      case 'right':
        if (!positionAnchorsState.enableLeft) {
          dom.style.left = `${dimensionOnResizeStartInPx.current.left}px`;
        }
        // props.right = undefined;//disable right on start but enable again on stop
        dom.style.right = 'unset';
        break;
      case 'top':
        if (!positionAnchorsState.enableBottom) {
          dom.style.bottom = `${dimensionOnResizeStartInPx.current.bottom}px`;
        }
        // props.top = undefined;//disable top on start but enable again on stop
        dom.style.top = 'unset';
        if (isAspectRatioLocked) {
          dom.style.right = 'unset';
        }
        break;
      case 'bottom':
        if (!positionAnchorsState.enableTop) {
          dom.style.top = `${dimensionOnResizeStartInPx.current.top}px`;
        }
        // props.bottom = undefined;//disable bottom on start but enable again on stop
        dom.style.bottom = 'unset';
        if (isAspectRatioLocked) {
          dom.style.right = 'unset';
        }
        break;
      case 'topLeft':
        // Lock opposite anchors (bottom and right)
        if (!positionAnchorsState.enableBottom) {
          dom.style.bottom = `${dimensionOnResizeStartInPx.current.bottom}px`;
        }
        if (!positionAnchorsState.enableRight) {
          dom.style.right = `${dimensionOnResizeStartInPx.current.right}px`;
        }
        // Disable top and left on start but enable again on stop
        dom.style.top = 'unset';
        dom.style.left = 'unset';
        break;
      case 'topRight':
        // Lock opposite anchors (bottom and left)
        if (!positionAnchorsState.enableBottom) {
          dom.style.bottom = `${dimensionOnResizeStartInPx.current.bottom}px`;
        }
        if (!positionAnchorsState.enableLeft) {
          dom.style.left = `${dimensionOnResizeStartInPx.current.left}px`;
        }
        // Disable top and right on start but enable again on stop
        dom.style.top = 'unset';
        dom.style.right = 'unset';
        break;
      case 'bottomLeft':
        // Lock opposite anchors (top and right)
        if (!positionAnchorsState.enableTop) {
          dom.style.top = `${dimensionOnResizeStartInPx.current.top}px`;
        }
        if (!positionAnchorsState.enableRight) {
          dom.style.right = `${dimensionOnResizeStartInPx.current.right}px`;
        }
        // Disable bottom and left on start but enable again on stop
        dom.style.bottom = 'unset';
        dom.style.left = 'unset';
        break;
      case 'bottomRight':
        // Lock opposite anchors (top and left)
        if (!positionAnchorsState.enableTop) {
          dom.style.top = `${dimensionOnResizeStartInPx.current.top}px`;
        }
        if (!positionAnchorsState.enableLeft) {
          dom.style.left = `${dimensionOnResizeStartInPx.current.left}px`;
        }
        // Disable bottom and right on start but enable again on stop
        dom.style.bottom = 'unset';
        dom.style.right = 'unset';
        break;
    }
  };

  const handleOnResizeAbsolute: ResizeCallback = (_, __, ___, deltaDimensions) => {
    didDrag.current = deltaDimensions.width !== 0 || deltaDimensions.height !== 0;

    // When aspect ratio is locked, re-resizable drives both dimensions via lockAspectRatio
  };

  const handleOnResizeStopAbsolute: ResizeCallback = (_, direction, ___, deltaDimensions) => {
    try {
      changeHeightCalled.current = false;
      const dom = resizable.current?.resizable;

      if (!dom) return;

      if (dom) {
        dom.classList.add('pointer-events-none');
      }

      updateInternalDimensionsWithOriginal();
      isResizing.current = false;

      if (!didDrag.current) return;

      const { newFinalWidthStr, newFinalHeightStr, left, top, right, bottom } =
        getNewFinalDimensions(deltaDimensions);

      setProp((props: { [key: string]: unknown; width?: string; height?: string }) => {
        // Update opposite anchor when the other dimension also changed (e.g. corner or lockAspectRatio)
        const updateOppositeAxisAnchors = () => {
          const parentElement = getBoundaryDomElement(parentNode?.dom as HTMLElement);
          const parentBounding = parentElement.getBoundingClientRect();
          const parentWidth = parentBounding.width / scale;
          const parentHeight = parentBounding.height / scale;

          if ((direction === 'left' || direction === 'right') && deltaDimensions.height !== 0) {
            const topPx = getBPProp(props as Partial<ContainerProps>, 'top');
            const topPxValue = topPx ? parseFloat(topPx.replace('px', '')) || 0 : 0;
            const heightPx = parseInt(String(newFinalHeightStr).replace('px', '')) || 0;
            const newBottomPx = Math.round(parentHeight - topPxValue - heightPx);
            setBPProp(props, 'bottom', `${newBottomPx}px`);
          } else if (
            (direction === 'top' || direction === 'bottom') &&
            deltaDimensions.width !== 0
          ) {
            const leftPx = getBPProp(props as Partial<ContainerProps>, 'left');
            const leftPxValue = leftPx ? parseFloat(leftPx.replace('px', '')) || 0 : 0;
            const widthPx = parseInt(String(newFinalWidthStr).replace('px', '')) || 0;
            const newRightPx = Math.round(parentWidth - leftPxValue - widthPx);
            setBPProp(props, 'right', `${newRightPx}px`);
          }
        };

        switch (direction) {
          case 'left':
            setBPProp(props, 'left', `${left}px`);
            //remove right on stop as we added temporarily on start
            if (!positionAnchorsState.enableRight) {
              dom.style.right = `unset`;
            }
            updateOppositeAxisAnchors();
            break;
          case 'right':
            setBPProp(props, 'right', `${right}px`);
            //remove left on stop as we added temporarily on start
            if (!positionAnchorsState.enableLeft && positionAnchorsState.enableRight) {
              //left should be unset only if right is enabled otherwise there will not be left/right anchor points
              dom.style.left = `unset`;
            }
            updateOppositeAxisAnchors();
            break;
          case 'top':
            setBPProp(props, 'top', `${top}px`);
            //remove bottom on stop as we added temporarily on start
            if (!positionAnchorsState.enableBottom) {
              dom.style.bottom = `unset`;
            }
            updateOppositeAxisAnchors();
            break;
          case 'bottom':
            setBPProp(props, 'bottom', `${bottom}px`);
            //remove top on stop as we added temporarily on start
            if (!positionAnchorsState.enableTop && positionAnchorsState.enableBottom) {
              //top should be unset only if bottom is enabled otherwise there will not be top/bottom anchor points
              dom.style.top = `unset`;
            }
            updateOppositeAxisAnchors();
            break;
          case 'topLeft':
            setBPProp(props, 'top', `${top}px`);
            setBPProp(props, 'left', `${left}px`);
            //remove bottom/right on stop as we added temporarily on start
            if (!positionAnchorsState.enableBottom) {
              dom.style.bottom = `unset`;
            }
            if (!positionAnchorsState.enableRight) {
              dom.style.right = `unset`;
            }
            break;
          case 'topRight':
            setBPProp(props, 'top', `${top}px`);
            setBPProp(props, 'right', `${right}px`);
            //remove bottom/left on stop as we added temporarily on start
            if (!positionAnchorsState.enableBottom) {
              dom.style.bottom = `unset`;
            }
            if (!positionAnchorsState.enableLeft && positionAnchorsState.enableRight) {
              //left should be unset only if right is enabled otherwise there will not be left/right anchor points
              dom.style.left = `unset`;
            }
            break;
          case 'bottomLeft':
            setBPProp(props, 'bottom', `${bottom}px`);
            setBPProp(props, 'left', `${left}px`);
            //remove top/right on stop as we added temporarily on start
            if (!positionAnchorsState.enableTop && positionAnchorsState.enableBottom) {
              //top should be unset only if bottom is enabled otherwise there will not be top/bottom anchor points
              dom.style.top = `unset`;
            }
            if (!positionAnchorsState.enableRight) {
              dom.style.right = `unset`;
            }
            break;
          case 'bottomRight':
            setBPProp(props, 'bottom', `${bottom}px`);
            setBPProp(props, 'right', `${right}px`);
            //remove top/left on stop as we added temporarily on start
            if (!positionAnchorsState.enableTop && positionAnchorsState.enableBottom) {
              //top should be unset only if bottom is enabled otherwise there will not be top/bottom anchor points
              dom.style.top = `unset`;
            }
            if (!positionAnchorsState.enableLeft && positionAnchorsState.enableRight) {
              //left should be unset only if right is enabled otherwise there will not be left/right anchor points
              dom.style.left = `unset`;
            }
            break;
        }

        if (positionAnchorsState.enableLeft && positionAnchorsState.enableRight) {
          setBPProp(props, propKey.width as keyof Partial<ContainerProps>, 'auto');
        } else if (deltaDimensions.width !== 0) {
          setBPProp(props, propKey.width as keyof Partial<ContainerProps>, newFinalWidthStr);
        }

        if (positionAnchorsState.enableTop && positionAnchorsState.enableBottom) {
          setBPProp(props, propKey.height as keyof Partial<ContainerProps>, 'auto');
        } else if (deltaDimensions.height !== 0) {
          setBPProp(props, propKey.height as keyof Partial<ContainerProps>, newFinalHeightStr);
        }
      });
    } finally {
      setMinHeight(0);
      setMaxHeight(0);
      setTimeout(() => {
        dispatch(setObjectResizing(false));
      }, 100);
    }
  };

  // Corner handles: anchor wrapper to exact corners (using margins only), then
  // center the square on the corner via translate. This avoids border/outline math.

  return (
    <Resizable
      enable={allowedControls}
      className={cx({
        flex: true,
      })}
      handleStyles={{
        bottom: {
          // margin[2] => bottom
          bottom: -5 + margin[2],
        },
        right: {
          // margin[1] => right
          right: -5 + margin[1],
        },
        top: {
          top: -5 + margin[0],
        },
        left: {
          // margin[3] => left
          left: -5 + margin[3],
        },
        topLeft: {
          top: 0 + margin[0],
          left: 0 + margin[3],
        },
        topRight: {
          top: 0 + margin[0],
          right: 0 + margin[1],
        },
        bottomLeft: {
          bottom: 0 + margin[2],
          left: 0 + margin[3],
        },
        bottomRight: {
          bottom: 0 + margin[2],
          right: 0 + margin[1],
        },
      }}
      handleComponent={{
        bottom: (
          <div
            className={`b-center pointer-events-auto absolute top-[3px] left-[50%] z-9999 h-1 w-10 -translate-x-1/2 rounded bg-white outline ${outlineColorClasses[getNodeThemeColorClass(nodeName)] || 'outline-indigo-500'}`}
          ></div>
        ),
        right: (
          <div
            className={`r-center pointer-events-auto absolute top-[50%] left-[3px] z-9999 h-10 w-1 -translate-y-1/2 rounded bg-white outline ${outlineColorClasses[getNodeThemeColorClass(nodeName)] || 'outline-indigo-500'}`}
          ></div>
        ),
        top: (
          <div
            className={`t-center pointer-events-auto absolute top-[3px] bottom-0 left-[50%] z-9999 h-1 w-10 -translate-x-1/2 rounded bg-white outline ${outlineColorClasses[getNodeThemeColorClass(nodeName)] || 'outline-indigo-500'}`}
          ></div>
        ),
        left: (
          <div
            className={`l-center pointer-events-auto absolute top-[50%] right-0 left-[4px] z-9999 h-10 w-1 -translate-y-1/2 rounded bg-white outline ${outlineColorClasses[getNodeThemeColorClass(nodeName)] || 'outline-indigo-500'}`}
          ></div>
        ),
        topLeft: (
          <div
            className={`pointer-events-auto absolute top-0 left-0 z-9999 h-2 w-2 -translate-x-1/2 -translate-y-1/2 bg-white outline ${outlineColorClasses[getNodeThemeColorClass(nodeName)] || 'outline-indigo-500'}`}
          />
        ),
        topRight: (
          <div
            className={`pointer-events-auto absolute top-0 right-0 z-9999 h-2 w-2 translate-x-1/2 -translate-y-1/2 bg-white outline ${outlineColorClasses[getNodeThemeColorClass(nodeName)] || 'outline-indigo-500'}`}
          />
        ),
        bottomLeft: (
          <div
            className={`pointer-events-auto absolute bottom-0 left-0 z-9999 h-2 w-2 -translate-x-1/2 translate-y-1/2 bg-white outline ${outlineColorClasses[getNodeThemeColorClass(nodeName)] || 'outline-indigo-500'}`}
          />
        ),
        bottomRight: (
          <div
            className={`pointer-events-auto absolute right-0 bottom-0 z-9999 h-2 w-2 translate-x-1/2 translate-y-1/2 bg-white outline ${outlineColorClasses[getNodeThemeColorClass(nodeName)] || 'outline-indigo-500'}`}
          />
        ),
      }}
      ref={(ref) => {
        if (onRef) {
          onRef(ref?.resizable as HTMLElement);
        }
        if (ref?.resizable) {
          resizable.current = ref;
        }
      }}
      size={
        isFullscreenPreview
          ? // WHY: In fullscreen preview the canvas IS the viewport. We compute the correct
            // responsive width in JS (see computeFullscreenSizeWidth) to match the published
            // page's @container query behaviour, because re-resizable's inline style.width
            // always overrides CSS class rules and would otherwise ignore breakpoint thresholds.
            { width: fullscreenSizeWidth, height: internalDimensions.height }
          : isAspectRatioLocked
            ? { width: internalDimensions.width, height: 'auto' } // re-resizable resolves height from DOM when lockAspectRatio is set
            : internalDimensions
      }
      lockAspectRatio={isAspectRatioLocked && aspectRatio ? aspectRatio : false}
      onResizeStart={isAbsolutelyPositioned ? handleResizeStartAbsolute : handleResizeStart}
      onResize={isAbsolutelyPositioned ? handleOnResizeAbsolute : handleOnResize}
      onResizeStop={isAbsolutelyPositioned ? handleOnResizeStopAbsolute : handleOnResizeStop}
      scale={scale}
      minHeight={minHeight}
      maxHeight={maxHeight || undefined}
      maxWidth={maxWidthLocal || undefined}
      {...props}
    >
      {children}
      {isImage && isActive && <ImageRadiusControls />}
    </Resizable>
  );
};
